import { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth0Safe as useAuth0 } from '@/hooks/useAuth0Safe';
import { Lock, LogIn, Shield, KeyRound } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { loginWithRedirect, isAuthenticated: auth0Authed } = useAuth0();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [alreadyLocal, setAlreadyLocal] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('emina_local_admin_token')) setAlreadyLocal(true);
  }, []);

  // Only redirect when we have a LOCAL admin JWT. A stale Auth0 session
  // that isn't actually admin would cause a /admin → / → /admin/login
  // loop because AdminLayout sends non-admins back to the homepage and
  // the browser would keep refreshing. The admin signs in here again.
  if (alreadyLocal) return <Navigate to="/admin" replace />;
  // Silence unused import warning — retained for future Auth0 toggle UI
  void auth0Authed;

  async function submit(e) {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API}/auth/local/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Neuspjela prijava');
      }
      const d = await r.json();
      localStorage.setItem('emina_local_admin_token', d.access_token);
      navigate('/admin');
    } catch (err) {
      setError(err.message || 'Greška prilikom prijave.');
    } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 text-gray-100 p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <Shield size={28} className="text-red-500" />
          <h1 className="text-2xl font-bold">E-MINA Admin</h1>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-2xl p-6">
          <h2 className="text-lg font-semibold mb-1 flex items-center gap-2"><Lock size={16} /> Prijava</h2>
          <p className="text-xs text-slate-400 mb-5">Lokalna admin prijava (fallback). Koristite Auth0 kad je dostupan.</p>

          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">E-mail</label>
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2.5 text-sm outline-none focus:border-red-400"
                data-testid="admin-login-email" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Lozinka</label>
              <input type="password" required value={password} onChange={e => setPassword(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2.5 text-sm outline-none focus:border-red-400"
                data-testid="admin-login-password" />
            </div>
            {error && <p className="text-xs text-red-400" data-testid="admin-login-error">{error}</p>}
            <button type="submit" disabled={loading || !email || !password}
              className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-md disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="admin-login-submit">
              <KeyRound size={14} /> {loading ? 'Prijavljujem…' : 'Prijavi se (lokalno)'}
            </button>
          </form>

          <div className="mt-5 pt-4 border-t border-slate-800">
            <button onClick={() => loginWithRedirect()}
              className="w-full py-2 border border-slate-700 hover:border-red-500 text-sm text-slate-300 hover:text-red-400 rounded-md flex items-center justify-center gap-2"
              data-testid="admin-login-auth0">
              <LogIn size={14} /> Prijava preko Auth0
            </button>
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-500 mt-5">
          <button type="button" onClick={() => navigate('/admin/forgot-password')}
                  data-testid="admin-login-forgot"
                  className="underline hover:text-red-400">
            Zaboravljena lozinka?
          </button>
          {' · '}
          pročitajte <code className="text-slate-300">/app/LOGIN_GUIDE.md</code>
        </p>
      </div>
    </div>
  );
}
